import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { logger as LOG } from '@garnish/logger';
import { useNoticeBannersStackActions } from '@garnish/overlays';
import { CombinedError } from 'urql';

import { RedeemGiftCardResponseUnion } from '@sg/graphql-schema';

import { checkIfAwsWafChallengeError } from '@order/integrations-aws-waf';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

const logger = LOG.extend('Gift Card Redemption');

// ────────────────────────────────────────────────────────────────────────────────

export const useHandleGiftCardResponse = () => {
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const addErrorBanner = useCallback(
    (id: string, text: string) => {
      addNoticeBanner({ id, text, palette: 'caution' }, true);
    },
    [addNoticeBanner],
  );

  const addSuccessBanner = useCallback(
    (text: string) => {
      addNoticeBanner({ text, palette: 'success' });
    },
    [addNoticeBanner],
  );

  const handleGiftCardResponse = useCallback(
    async (params: HandleGiftCardResponseParams) => {
      const { response, error, source } = params;

      const responseTypename = response?.__typename;

      // ─────────────────────────────────────────────────────────────────────

      const hasNetworkError = error !== undefined;
      const isUnableToRedeemGiftCard = responseTypename === 'UnableToRedeemGiftCardError';
      const isInvalidGiftCard = responseTypename === 'InvalidGiftCardError';
      const isAlreadyRedeemedGiftCard = responseTypename === 'GiftCardAssociatedWithAnotherAccountError'; // prettier-ignore
      const hasRedeemedGiftCard = responseTypename === 'GiftCardBalance';
      const isNoBalanceError = responseTypename === 'NoBalanceGiftCardError';

      // ─────────────────────────────────────────────────────────────────────

      if (hasRedeemedGiftCard) {
        track('gift-card.redemption.success', { source });
        addSuccessBanner(formatMessage(messages.submitSuccess));

        return true;
      }

      const isChallengeError = checkIfAwsWafChallengeError(error);

      track('gift-card.redemption.failure', {
        source,
        systemError: getUrqlError(error),
        userError: responseTypename,
        isChallengeError,
      });

      if (isChallengeError) {
        logger.error('Gift card redemption failed due to AWS WAF Challenge', { error });
      }

      if (hasNetworkError || isUnableToRedeemGiftCard) {
        addErrorBanner(
          'unable-to-redeem-gift-card-error-banner',
          formatMessage(messages.submitUnableToRedeemError),
        );

        return false;
      }

      if (isNoBalanceError) {
        addErrorBanner(
          'no-balance-gift-card-error-banner',
          formatMessage(messages.submitNoRemainingBalanceError),
        );

        return false;
      }

      if (isInvalidGiftCard) {
        addErrorBanner(
          'invalid-gift-card-error-banner',
          formatMessage(messages.submitInvalidGiftCardError),
        );

        return false;
      }

      if (isAlreadyRedeemedGiftCard) {
        addErrorBanner(
          'already-applied-gift-card-error-banner',
          formatMessage(messages.submitAlreadyAppliedError),
        );

        return false;
      }

      return false;
    },
    [track, formatMessage, addErrorBanner, addSuccessBanner],
  );

  return { handleGiftCardResponse };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  submitSuccess: {
    defaultMessage: 'Credit added successfully',
    description: 'Gift Card | Redeem | Submit success',
  },
  submitInvalidGiftCardError: {
    defaultMessage: 'This code or gift card is not valid.',
    description: 'Gift Card | Redeem | Submit invalid gift card error',
  },
  submitAlreadyAppliedError: {
    defaultMessage: 'This code or gift card has already been applied.',
    description: 'Gift Card | Redeem | Submit already applied error',
  },
  submitUnableToRedeemError: {
    defaultMessage: 'Unable to redeem gift card. Please try again later.',
    description: 'Gift Card | Redeem | Submit unable to redeem error',
  },
  submitNoRemainingBalanceError: {
    defaultMessage: 'This gift card has no remaining balance to transfer to your digital account.',
    description: 'Gift Card | Redeem | Submit no remaining balance error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type HandleGiftCardResponseParams = {
  response?: RedeemGiftCardResponseUnion;
  error?: CombinedError;
  source: 'account' | 'bag';
};
